import React, { useEffect, useMemo, useState } from "react";
import { MAKES_DATA, PARTS_DATA } from "./Data";

const STORE_ID = "cmr7wh4ey030do35ydeft8ukq";
const API_KEY = "4560a5d74bfb3810ee113b21f210546d";

const BASE_URL = `https://api-sb.allgoodparts.com.au/api/storefront/stores/${STORE_ID}/parts`;
const MAIN_DOMAIN = "https://allgoodparts.com.au";

/* =========================================================
   HELPERS
========================================================= */

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[()]/g, "")
    .replace(/[./_]+/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function prettyName(value) {
  return String(value || "")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/* =========================================================
   CREATE FAST IMAGE LOOKUPS FROM Data.js
========================================================= */

const PART_IMAGE_MAP = new Map(
  PARTS_DATA.map((item) => [
    normalize(item.name),
    item.image,
  ])
);

const MAKE_IMAGE_MAP = new Map(
  MAKES_DATA.map((item) => [
    normalize(item.name),
    item.image,
  ])
);

/* =========================================================
   IMAGE HELPERS
========================================================= */

function getPartImage(name) {
  const key = normalize(name);

  // Exact match first
  if (PART_IMAGE_MAP.has(key)) {
    return PART_IMAGE_MAP.get(key);
  }

  // Try partial match
  const match = PARTS_DATA.find((item) => {
    const itemKey = normalize(item.name);

    return (
      itemKey === key ||
      itemKey.includes(key) ||
      key.includes(itemKey)
    );
  });

  return match?.image || "/parts/misc.avif";
}

function getMakeImage(name) {
  const key = normalize(name);

  // Exact match first
  if (MAKE_IMAGE_MAP.has(key)) {
    return MAKE_IMAGE_MAP.get(key);
  }

  // Try partial match
  const match = MAKES_DATA.find((item) => {
    const itemKey = normalize(item.name);

    return (
      itemKey === key ||
      itemKey.includes(key) ||
      key.includes(itemKey)
    );
  });

  return match?.image || "";
}

/* =========================================================
   API FACET HELPERS
========================================================= */

function getFacetValues(facet) {
  if (!facet) return [];

  if (Array.isArray(facet)) {
    return facet;
  }

  if (Array.isArray(facet.selected)) {
    return facet.selected;
  }

  if (Array.isArray(facet.values)) {
    return facet.values;
  }

  if (Array.isArray(facet.options)) {
    return facet.options;
  }

  if (Array.isArray(facet.items)) {
    return facet.items;
  }

  return [];
}

function cleanValues(values) {
  const map = new Map();

  values.forEach((item) => {
    let value = item;

    if (typeof item === "object" && item !== null) {
      value =
        item.value ||
        item.name ||
        item.label ||
        item.title ||
        "";
    }

    if (!value) return;

    const text = String(value).trim();

    if (!text) return;

    const key = normalize(text);

    if (!map.has(key)) {
      map.set(key, text);
    }
  });

  return Array.from(map.values());
}

/* =========================================================
   DIRECTORY ITEM
========================================================= */

function DirectoryItem({ name, type }) {
  const image =
    type === "part"
      ? getPartImage(name)
      : getMakeImage(name);

  const filter =
    type === "part"
      ? "filter.p.m.custom.part"
      : "filter.p.m.custom.make";

  const url =
    `${MAIN_DOMAIN}/collections/all?` +
    `${filter}=${encodeURIComponent(name)}`;

  return (
    <a
      href={url}
      className="group flex min-w-[130px] flex-shrink-0 flex-col items-center justify-center text-center"
    >
      {type === "part" ? (
        <div className="mb-3 flex h-[78px] w-[78px] items-center justify-center rounded-full border border-[#ffd9dc] bg-[#fff6f6] transition-all duration-200 group-hover:border-[#ffb7bd] group-hover:bg-[#fff0f1]">
          <img
            src={image}
            alt={prettyName(name)}
            loading="lazy"
            className="h-[50px] w-[50px] object-contain transition-transform duration-200 group-hover:scale-110"
            onError={(event) => {
              if (
                !event.currentTarget.src.endsWith(
                  "/parts/misc.avif"
                )
              ) {
                event.currentTarget.src =
                  "/parts/misc.avif";
              }
            }}
          />
        </div>
      ) : (
        <div className="mb-3 flex h-[78px] w-[110px] items-center justify-center rounded-md border border-[#eeeeee] bg-white px-3 transition-all duration-200 group-hover:border-[#dddddd] group-hover:shadow-sm">
          {image ? (
            <img
              src={image}
              alt={prettyName(name)}
              loading="lazy"
              className="max-h-[48px] max-w-[82px] object-contain transition-transform duration-200 group-hover:scale-105"
              onError={(event) => {
                event.currentTarget.style.display =
                  "none";
              }}
            />
          ) : (
            <span className="text-[13px] font-bold text-gray-400">
              {prettyName(name).substring(0, 2)}
            </span>
          )}
        </div>
      )}

      <span className="max-w-[125px] text-[12px] font-bold capitalize leading-[1.35] text-[#111] transition-colors group-hover:text-[#e31b23]">
        {prettyName(name)}
      </span>
    </a>
  );
}

/* =========================================================
   HORIZONTAL DIRECTORY SECTION
========================================================= */

function DirectorySection({
  title,
  accent,
  items,
  type,
}) {
  const scrollId = `directory-${type}`;

  const scroll = (direction) => {
    const element =
      document.getElementById(scrollId);

    if (!element) return;

    element.scrollBy({
      left: direction === "left" ? -600 : 600,
      behavior: "smooth",
    });
  };

  if (!items.length) {
    return null;
  }

  return (
    <section className="rounded-lg bg-white px-5 py-5 md:px-6">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="text-[18px] font-bold text-[#111]">
          {title}{" "}
          <span className="text-[#f32835]">
            {accent}
          </span>
        </h3>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => scroll("left")}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-lg text-gray-600 transition hover:border-[#e31b23] hover:text-[#e31b23]"
            aria-label="Scroll left"
          >
            ‹
          </button>

          <button
            type="button"
            onClick={() => scroll("right")}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-lg text-gray-600 transition hover:border-[#e31b23] hover:text-[#e31b23]"
            aria-label="Scroll right"
          >
            ›
          </button>
        </div>
      </div>

      <div
        id={scrollId}
        className="flex gap-5 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((name) => (
          <DirectoryItem
            key={`${type}-${normalize(name)}`}
            name={name}
            type={type}
          />
        ))}
      </div>
    </section>
  );
}

/* =========================================================
   LOADING SKELETON
========================================================= */

function LoadingSkeleton() {
  return (
    <section className="rounded-lg bg-white p-6">
      <div className="mb-6 h-6 w-40 animate-pulse rounded bg-gray-200" />

      <div className="flex gap-5 overflow-hidden">
        {Array.from({ length: 7 }).map(
          (_, index) => (
            <div
              key={index}
              className="flex min-w-[130px] flex-col items-center"
            >
              <div className="h-[78px] w-[78px] animate-pulse rounded-full bg-gray-100" />

              <div className="mt-3 h-3 w-20 animate-pulse rounded bg-gray-100" />
            </div>
          )
        )}
      </div>
    </section>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function PartsDirectoryTest() {
  const [parts, setParts] = useState([]);
  const [makes, setMakes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadDirectory() {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(BASE_URL, {
          method: "GET",

          headers: {
            "x-storefront-key": API_KEY,
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Parts API failed: ${response.status}`
          );
        }

        const data = await response.json();

        console.log(
          "Parts directory API response:",
          data
        );

        const facets =
          data?.facets ||
          data?.data?.facets ||
          {};

        console.log(
          "Parts directory facets:",
          facets
        );

        const partValues = cleanValues(
          getFacetValues(facets.part)
        );

        const makeValues = cleanValues(
          getFacetValues(facets.make)
        );

        if (!mounted) return;

        setParts(partValues);
        setMakes(makeValues);

        console.log(
          "Parts returned from API:",
          partValues
        );

        console.log(
          "Makes returned from API:",
          makeValues
        );
      } catch (err) {
        console.error(
          "PartsDirectoryTest error:",
          err
        );

        if (!mounted) return;

        setError(
          err?.message ||
            "Unable to load parts."
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadDirectory();

    return () => {
      mounted = false;
    };
  }, []);

  /* =======================================================
     SORT DATA
  ======================================================= */

  const sortedParts = useMemo(() => {
    return [...parts].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [parts]);

  const sortedMakes = useMemo(() => {
    return [...makes].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [makes]);

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="space-y-2">
        <LoadingSkeleton />
        <LoadingSkeleton />
      </div>
    );
  }

  /* =======================================================
     ERROR
  ======================================================= */

  if (error) {
    return (
      <section className="rounded-lg bg-white p-6 text-center">
        <p className="text-sm font-medium text-red-600">
          {error}
        </p>

        <button
          type="button"
          onClick={() =>
            window.location.reload()
          }
          className="mt-4 rounded-md bg-[#e31b23] px-5 py-2 text-sm font-semibold text-white hover:bg-[#c9161d]"
        >
          Try Again
        </button>
      </section>
    );
  }

  /* =======================================================
     EMPTY
  ======================================================= */

  if (
    !sortedParts.length &&
    !sortedMakes.length
  ) {
    return (
      <section className="rounded-lg bg-white p-6 text-center">
        <p className="text-sm text-gray-500">
          No parts or makes were returned by
          the API.
        </p>
      </section>
    );
  }

  /* =======================================================
     OUTPUT
  ======================================================= */

  return (
    <div className="space-y-2">
      <DirectorySection
        title="Browse By"
        accent="Parts"
        items={sortedParts}
        type="part"
      />

      <DirectorySection
        title="Browse By"
        accent="Make & Models"
        items={sortedMakes}
        type="make"
      />
    </div>
  );
}